/**
 * 甘特圖主元件。
 *
 * 純展示元件：props in / callbacks out，零 store 相依、零 Tauri 相依。
 * 縱向與橫向皆為虛擬化渲染，只產生可視範圍的列、刻度與依賴線。
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactElement,
  type UIEvent,
} from 'react';

import type { Dependency, ISODateTime, TaskId } from '@bigint-project/core';

import {
  anchorAtX,
  anchorX,
  linkTargetRow,
  movedDates,
  progressFromDrag,
  resizedDates,
} from './drag-math.js';
import {
  routeDependency,
  predecessorAnchorOf,
  successorAnchorOf,
} from './dependency-path.js';
import {
  barGeometry,
  barSpan,
  isBarVisible,
  progressBand,
  type BarGeometry,
} from './geometry.js';
import { TaskBar } from './TaskBar.js';
import { HEADER_HEIGHT, TimelineHeader } from './TimelineHeader.js';
import {
  PX_PER_DAY,
  clampPxPerDay,
  computeTimeAxis,
  generateTicks,
  parseDateTime,
  scaleForPxPerDay,
  timeToX,
  weekendBands,
  xToTime,
  type TimeAxis,
} from './time-scale.js';
import { dependencyTypeFor, type DragKind, type GanttChartProps, type GanttTask, type LinkAnchor } from './types.js';
import { visibleRowRange, visibleTimeRange } from './virtualization.js';

/** 依賴箭頭尖端與任務條邊緣之間留的間隙。 */
const ARROW_CLEARANCE = 2;

/**
 * 依賴線端點：條的實際邊緣（里程碑取菱形外緣），可再往外推 `clearance`。
 *
 * 與 `anchorX` 的差別只在視覺留白；建立依賴的判定仍走 `anchorX`。
 */
function edgeX(bar: BarGeometry, anchor: LinkAnchor, clearance: number): number {
  const { left, right } = barSpan(bar);
  return anchor === 'start' ? left - clearance : right + clearance;
}

const DEFAULT_VIEWPORT_WIDTH = 960;
const DEFAULT_VIEWPORT_HEIGHT = 600;
/** 超過此像素位移才視為拖曳（否則當成點選）。 */
const DRAG_THRESHOLD = 3;
/**
 * Ctrl/Cmd + 滾輪縮放的靈敏度：`pxPerDay *= exp(-deltaY * 係數)`。
 * 一般滑鼠滾輪一格 deltaY 約 100，此係數讓一格約縮放 ±25%，滑順且不會一格跳很大。
 */
const ZOOM_WHEEL_SENSITIVITY = 0.0025;

/**
 * 按下滑鼠當下就凍結整段手勢需要的資料。
 *
 * 拖曳的語意是「以抓取當下看到的畫面為準」，因此 tasks / axis / 版面參數
 * 全部在 pointer-down 時快照；提交時不再回頭讀可能已經改變（或尚未同步）的 props。
 */
interface DragSnapshot {
  readonly tasks: readonly GanttTask[];
  readonly axis: TimeAxis;
  readonly rowHeight: number;
  readonly showBaseline: boolean;
}

interface DragState {
  readonly kind: DragKind;
  readonly task: GanttTask;
  readonly bar: BarGeometry;
  readonly rowIndex: number;
  readonly anchor: LinkAnchor | null;
  readonly clientX: number;
  readonly clientY: number;
  readonly deltaX: number;
  readonly deltaY: number;
  readonly snapshot: DragSnapshot;
}

export function GanttChart(props: GanttChartProps): ReactElement {
  const {
    tasks,
    dependencies,
    timeScale,
    rowHeight,
    showBaseline,
    showCriticalPath,
    dependencyLineMode = 'all',
    scrollTop,
    onScrollTopChange,
    onTaskDateChange,
    onTaskProgressChange,
    onDependencyCreate,
    onTaskSelect,
    selectedTaskIds,
    overscanRows = 4,
    className,
    onEffectiveTimeScaleChange,
  } = props;

  const todayMs = useMemo(
    () => (props.today === undefined ? Date.now() : parseDateTime(props.today)),
    [props.today],
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [measured, setMeasured] = useState({
    width: DEFAULT_VIEWPORT_WIDTH,
    height: DEFAULT_VIEWPORT_HEIGHT,
  });

  const viewportWidth = props.viewportWidth ?? measured.width;
  const viewportHeight = props.viewportHeight ?? measured.height;

  // --- 連續縮放（Ctrl/Cmd + 滾輪；四個下拉選單刻度只決定表頭顯示哪種單位） ---
  const [pxPerDay, setPxPerDay] = useState(() => PX_PER_DAY[timeScale]);
  // 下拉選單切換 timeScale 時，滾輪縮放重新從該刻度的預設寬度繼續。
  useEffect(() => {
    setPxPerDay(PX_PER_DAY[timeScale]);
  }, [timeScale]);
  const effectiveScale = scaleForPxPerDay(pxPerDay);
  // 只在「實際生效的刻度」跨過切點而改變時通知外部（例如下拉選單）；
  // 用 lazy init 記下第一次算出的值，掛載當下不算「改變」，不會誤觸發一次。
  const lastReportedScaleRef = useRef(effectiveScale);
  useEffect(() => {
    if (lastReportedScaleRef.current === effectiveScale) return;
    lastReportedScaleRef.current = effectiveScale;
    onEffectiveTimeScaleChange?.(effectiveScale);
  }, [effectiveScale, onEffectiveTimeScaleChange]);

  // --- 量測容器（未由 props 指定時） ---
  useEffect(() => {
    const element = scrollRef.current;
    if (element === null) return;
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      setMeasured({ width: element.clientWidth, height: element.clientHeight });
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);

  // --- 外部控制的垂直捲動 ---
  useEffect(() => {
    const element = scrollRef.current;
    if (element !== null && Math.abs(element.scrollTop - scrollTop) > 0.5) {
      element.scrollTop = scrollTop;
    }
  }, [scrollTop]);

  const axis = useMemo<TimeAxis>(() => {
    const ranges = tasks.map((task) => ({
      startMs: parseDateTime(task.start),
      finishMs: parseDateTime(task.finish),
    }));
    return computeTimeAxis(ranges, effectiveScale, todayMs, pxPerDay);
  }, [tasks, effectiveScale, todayMs, pxPerDay]);

  const rowIndexById = useMemo(() => {
    const map = new Map<string, number>();
    tasks.forEach((task, index) => {
      map.set(task.id, index);
    });
    return map;
  }, [tasks]);

  const selected = useMemo(() => new Set<string>(selectedTaskIds), [selectedTaskIds]);

  const totalHeight = tasks.length * rowHeight;
  const rowWindow = visibleRowRange(
    scrollTop,
    viewportHeight,
    rowHeight,
    tasks.length,
    overscanRows,
  );
  // --- Ctrl/Cmd + 滾輪縮放：以游標所在的時間點為錨點 ---
  const axisRef = useRef(axis);
  axisRef.current = axis;
  const scrollLeftRef = useRef(scrollLeft);
  scrollLeftRef.current = scrollLeft;
  const pxPerDayRef = useRef(pxPerDay);
  pxPerDayRef.current = pxPerDay;
  const viewportWidthRef = useRef(viewportWidth);
  viewportWidthRef.current = viewportWidth;
  /** 縮放當下記下的錨點；縮放後的 layout effect 用它把畫面拉回同一個日期。 */
  const pendingZoomAnchorRef = useRef<{ ms: number; offsetX: number } | null>(null);

  useEffect(() => {
    const element = scrollRef.current;
    if (element === null) return;
    const onWheel = (event: WheelEvent): void => {
      // 沒按 Ctrl/Cmd：一般捲動，不攔截、不 preventDefault，交給瀏覽器原生行為。
      // macOS 觸控板雙指縮放在 WebView 中同樣以 ctrlKey=true 的 wheel 事件送出，
      // 因此這裡順帶支援雙指縮放，不需另外處理 gesture 事件。
      if (!(event.ctrlKey || event.metaKey)) return;
      event.preventDefault();
      const rect = element.getBoundingClientRect();
      const offsetX = event.clientX - rect.left;
      const cursorMs = xToTime(axisRef.current, scrollLeftRef.current + offsetX);
      // deltaY < 0（往上滾／pinch 放大）→ 放大；deltaY > 0 → 縮小。
      const factor = Math.exp(-event.deltaY * ZOOM_WHEEL_SENSITIVITY);
      const nextPxPerDay = clampPxPerDay(pxPerDayRef.current * factor);
      if (nextPxPerDay === pxPerDayRef.current) return;
      pendingZoomAnchorRef.current = { ms: cursorMs, offsetX };
      setPxPerDay(nextPxPerDay);
    };
    element.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      element.removeEventListener('wheel', onWheel);
    };
  }, []);

  // 縮放造成 axis 改變後，把錨點日期拉回滾輪事件當下的視窗位置。
  useLayoutEffect(() => {
    const anchor = pendingZoomAnchorRef.current;
    pendingZoomAnchorRef.current = null;
    if (anchor === null) return;
    const element = scrollRef.current;
    if (element === null) return;
    const maxScrollLeft = Math.max(0, axis.totalWidth - viewportWidthRef.current);
    const nextScrollLeft = Math.min(
      maxScrollLeft,
      Math.max(0, timeToX(axis, anchor.ms) - anchor.offsetX),
    );
    element.scrollLeft = nextScrollLeft;
    setScrollLeft(nextScrollLeft);
  }, [axis]);

  const timeWindow = visibleTimeRange(axis, scrollLeft, viewportWidth);
  const ticks = useMemo(
    () => generateTicks(axis, timeWindow.fromMs, timeWindow.toMs),
    [axis, timeWindow.fromMs, timeWindow.toMs],
  );
  const bands = useMemo(
    () => weekendBands(axis, timeWindow.fromMs, timeWindow.toMs),
    [axis, timeWindow.fromMs, timeWindow.toMs],
  );

  const layout = { rowHeight, showBaseline };
  const geometryAt = useCallback(
    (index: number): BarGeometry | null => {
      const task = tasks[index];
      if (task === undefined) return null;
      return barGeometry(task, index, axis, { rowHeight, showBaseline });
    },
    [tasks, axis, rowHeight, showBaseline],
  );

  // --- 拖曳 ---
  const dragRef = useRef<DragState | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  /** 拖曳後緊接的 click 屬於同一個任務，需忽略以免誤觸選取。 */
  const suppressClickRef = useRef<string | null>(null);

  /**
   * 提交拖曳結果時需要的 callback。
   *
   * 必須用 `useLayoutEffect` 同步更新：passive effect 有可能還沒 flush，
   * pointer-up 就已經發生（快速拖放），那樣會呼叫到過期的 callback。
   * layout effect 與 commit 在同一個 task 內同步執行，不會被 pointer 事件插隊。
   */
  const callbacksRef = useRef({
    onTaskDateChange,
    onTaskProgressChange,
    onDependencyCreate,
  });
  useLayoutEffect(() => {
    callbacksRef.current = {
      onTaskDateChange,
      onTaskProgressChange,
      onDependencyCreate,
    };
  });

  const handleDragStart = useCallback(
    (
      kind: DragKind,
      task: GanttTask,
      bar: BarGeometry,
      anchor: LinkAnchor | null,
      event: ReactPointerEvent,
    ): void => {
      event.stopPropagation();
      const rowIndex = rowIndexById.get(task.id) ?? 0;
      const next: DragState = {
        kind,
        task,
        bar,
        rowIndex,
        anchor,
        clientX: event.clientX,
        clientY: event.clientY,
        deltaX: 0,
        deltaY: 0,
        snapshot: { tasks, axis, rowHeight, showBaseline },
      };
      dragRef.current = next;
      setDrag(next);
    },
    [rowIndexById, tasks, axis, rowHeight, showBaseline],
  );

  const isDragging = drag !== null;
  useEffect(() => {
    if (!isDragging) return;

    const update = (event: PointerEvent): DragState | null => {
      const current = dragRef.current;
      if (current === null) return null;
      const next: DragState = {
        ...current,
        deltaX: event.clientX - current.clientX,
        deltaY: event.clientY - current.clientY,
      };
      dragRef.current = next;
      setDrag(next);
      return next;
    };

    const onMove = (event: PointerEvent): void => {
      const next = update(event);
      if (
        next !== null &&
        (Math.abs(next.deltaX) > DRAG_THRESHOLD || Math.abs(next.deltaY) > DRAG_THRESHOLD)
      ) {
        suppressClickRef.current = next.task.id;
      }
    };

    const onUp = (event: PointerEvent): void => {
      const next = update(event) ?? dragRef.current;
      dragRef.current = null;
      setDrag(null);
      if (next === null) return;
      commitDrag(next, callbacksRef.current);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [isDragging]);

  const handleSelect = useCallback(
    (task: GanttTask, additive: boolean): void => {
      if (suppressClickRef.current === task.id) {
        suppressClickRef.current = null;
        return;
      }
      suppressClickRef.current = null;
      onTaskSelect(task.id, additive);
    },
    [onTaskSelect],
  );

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>): void => {
      const element = event.currentTarget;
      setScrollLeft(element.scrollLeft);
      if (Math.abs(element.scrollTop - scrollTop) > 0.5) {
        onScrollTopChange(element.scrollTop);
      }
    },
    [onScrollTopChange, scrollTop],
  );

  // --- 可視列 ---
  const visibleRows: { task: GanttTask; index: number; bar: BarGeometry }[] = [];
  for (let index = rowWindow.startIndex; index < rowWindow.endIndex; index += 1) {
    const task = tasks[index];
    if (task === undefined) continue;
    const bar = barGeometry(task, index, axis, layout);
    if (!isBarVisible(bar, timeWindow.viewLeft, timeWindow.viewRight)) continue;
    visibleRows.push({ task, index, bar });
  }

  // --- 可視依賴線（任一端在可視列範圍內即繪製） ---
  const visibleDependencies = useMemo(() => {
    const result: { dep: Dependency; d: string; arrow: string; critical: boolean }[] = [];
    if (dependencyLineMode === 'none') return result;
    for (const dep of dependencies) {
      if (
        dependencyLineMode === 'selected' &&
        !selected.has(dep.predecessorId) &&
        !selected.has(dep.successorId)
      ) {
        continue;
      }
      const predIndex = rowIndexById.get(dep.predecessorId);
      const succIndex = rowIndexById.get(dep.successorId);
      if (predIndex === undefined || succIndex === undefined) continue;
      const inWindow =
        (predIndex >= rowWindow.startIndex && predIndex < rowWindow.endIndex) ||
        (succIndex >= rowWindow.startIndex && succIndex < rowWindow.endIndex);
      if (!inWindow) continue;
      const predBar = geometryAt(predIndex);
      const succBar = geometryAt(succIndex);
      if (predBar === null || succBar === null) continue;
      const from = {
        x: edgeX(predBar, predecessorAnchorOf(dep.type), 0),
        y: predBar.midY,
      };
      // 箭頭尖端停在條的邊緣外，不戳進任務條裡。
      const to = {
        x: edgeX(succBar, successorAnchorOf(dep.type), ARROW_CLEARANCE),
        y: succBar.midY,
      };
      if (
        Math.max(from.x, to.x) < timeWindow.viewLeft ||
        Math.min(from.x, to.x) > timeWindow.viewRight
      ) {
        continue;
      }
      const route = routeDependency({ from, to, type: dep.type, rowHeight });
      const predTask = tasks[predIndex];
      const succTask = tasks[succIndex];
      const critical =
        showCriticalPath &&
        predTask !== undefined &&
        succTask !== undefined &&
        predTask.isCritical &&
        succTask.isCritical;
      result.push({ dep, d: route.d, arrow: route.arrowPoints, critical });
    }
    return result;
  }, [
    dependencies,
    dependencyLineMode,
    selected,
    rowIndexById,
    rowWindow.startIndex,
    rowWindow.endIndex,
    geometryAt,
    rowHeight,
    showCriticalPath,
    tasks,
    timeWindow.viewLeft,
    timeWindow.viewRight,
  ]);

  const todayX = timeToX(axis, todayMs);
  const showToday = todayMs >= axis.originMs && todayMs <= axis.endMs;

  return (
    <div
      className={`flex h-full min-h-0 flex-col bg-surface text-fg ${className ?? ''}`}
      data-testid="gantt-chart"
    >
      <TimelineHeader
        axis={axis}
        ticks={ticks}
        todayMs={todayMs}
        scrollLeft={scrollLeft}
        width={viewportWidth}
      />
      <div
        ref={scrollRef}
        className="relative min-h-0 flex-1 overflow-auto"
        style={{ height: `calc(100% - ${HEADER_HEIGHT}px)` }}
        onScroll={handleScroll}
        data-testid="gantt-scroll"
      >
        <svg
          width={axis.totalWidth}
          height={Math.max(totalHeight, 1)}
          className="block select-none"
          data-testid="gantt-canvas"
        >
          {/* 背景：非工作日 */}
          <g data-testid="gantt-bands">
            {bands.map((band) => (
              <rect
                key={band.startMs}
                x={band.x}
                y={rowWindow.startIndex * rowHeight}
                width={band.width}
                height={(rowWindow.endIndex - rowWindow.startIndex) * rowHeight}
                className="fill-band"
              />
            ))}
          </g>
          {/* 背景：期間格線（下層極淡、上層期間界線略深，僅是暗示） */}
          <g data-testid="gantt-grid">
            {ticks.lower.map((tick) => (
              <line
                key={tick.startMs}
                x1={tick.x}
                y1={rowWindow.startIndex * rowHeight}
                x2={tick.x}
                y2={rowWindow.endIndex * rowHeight}
                className="stroke-grid-minor"
                strokeWidth={1}
                shapeRendering="crispEdges"
              />
            ))}
            {ticks.upper.map((tick) => (
              <line
                key={`period-${tick.startMs}`}
                x1={tick.x}
                y1={rowWindow.startIndex * rowHeight}
                x2={tick.x}
                y2={rowWindow.endIndex * rowHeight}
                className="stroke-grid-major"
                strokeWidth={1}
                shapeRendering="crispEdges"
              />
            ))}
            {Array.from(
              { length: Math.max(0, rowWindow.endIndex - rowWindow.startIndex) },
              (_, offset) => {
                const index = rowWindow.startIndex + offset;
                return (
                  <line
                    key={`row-${index}`}
                    x1={timeWindow.viewLeft}
                    y1={(index + 1) * rowHeight}
                    x2={timeWindow.viewRight}
                    y2={(index + 1) * rowHeight}
                    className="stroke-hairline"
                    strokeWidth={1}
                    shapeRendering="crispEdges"
                  />
                );
              },
            )}
          </g>
          {showToday ? (
            <line
              data-testid="gantt-today-line"
              x1={todayX}
              y1={rowWindow.startIndex * rowHeight}
              x2={todayX}
              y2={rowWindow.endIndex * rowHeight}
              className="stroke-accent"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
          ) : null}

          {/* 依賴線 */}
          <g data-testid="gantt-dependencies">
            {visibleDependencies.map((item) => (
              <g
                key={item.dep.id}
                data-testid="gantt-dependency"
                data-dep-id={item.dep.id}
                data-dep-type={item.dep.type}
                data-critical={item.critical ? 'true' : 'false'}
              >
                <path
                  d={item.d}
                  fill="none"
                  className={
                    item.critical
                      ? 'stroke-danger'
                      : 'stroke-fg-subtle'
                  }
                  strokeWidth={1}
                />
                <polygon
                  points={item.arrow}
                  className={
                    item.critical
                      ? 'fill-danger'
                      : 'fill-fg-subtle'
                  }
                />
              </g>
            ))}
          </g>

          {/* 任務條 */}
          <g data-testid="gantt-bars">
            {visibleRows.map(({ task, bar }) => (
              <TaskBar
                key={task.id}
                task={task}
                bar={bar}
                axis={axis}
                selected={selected.has(task.id)}
                showBaseline={showBaseline}
                showCriticalPath={showCriticalPath}
                onDragStart={handleDragStart}
                onSelect={handleSelect}
              />
            ))}
          </g>

          {/* 拖曳預覽 */}
          {drag === null ? null : (
            <DragPreview drag={drag} axis={drag.snapshot.axis} rowHeight={rowHeight} />
          )}
        </svg>
      </div>
    </div>
  );
}

interface DragPreviewProps {
  readonly drag: DragState;
  readonly axis: TimeAxis;
  readonly rowHeight: number;
}

/** 拖曳中的即時預覽（已套用吸附）。 */
function DragPreview({ drag, axis, rowHeight }: DragPreviewProps): ReactElement | null {
  const { bar, kind, deltaX, deltaY, task } = drag;
  if (kind === 'link') {
    const startX = anchorX(bar, drag.anchor ?? 'finish');
    return (
      <line
        data-testid="gantt-link-preview"
        x1={startX}
        y1={bar.midY}
        x2={startX + deltaX}
        y2={bar.midY + deltaY}
        className="stroke-focus"
        strokeWidth={1.5}
        strokeDasharray="4 2"
      />
    );
  }
  if (kind === 'progress') {
    const width = Math.max(0, Math.min(bar.width, bar.progressWidth + deltaX));
    const band = progressBand(bar);
    return (
      <rect
        data-testid="gantt-progress-preview"
        x={bar.x}
        y={band.y}
        width={width}
        height={band.height}
        className="fill-accent/80"
        pointerEvents="none"
      />
    );
  }
  const dates = kind === 'move' ? movedDates(task, axis, deltaX) : resizedDates(task, axis, deltaX);
  const x = timeToX(axis, parseDateTime(dates.start));
  const endX = timeToX(axis, parseDateTime(dates.finish));
  return (
    <rect
      data-testid="gantt-drag-preview"
      x={x}
      y={bar.y}
      width={Math.max(2, endX - x)}
      height={Math.max(bar.height, rowHeight * 0.3)}
      rx={1}
      className="fill-focus/20 stroke-focus"
      strokeWidth={1}
      strokeDasharray="3 2"
      pointerEvents="none"
    />
  );
}

interface DragCallbacks {
  readonly onTaskDateChange: (
    taskId: TaskId,
    start: ISODateTime,
    finish: ISODateTime,
  ) => void;
  readonly onTaskProgressChange: GanttChartProps['onTaskProgressChange'];
  readonly onDependencyCreate: GanttChartProps['onDependencyCreate'];
}

/** 放開滑鼠時把拖曳結果換算成 callback。 */
function commitDrag(drag: DragState, callbacks: DragCallbacks): void {
  const { kind, task, bar, deltaX, deltaY, rowIndex, anchor, snapshot } = drag;
  if (Math.abs(deltaX) < DRAG_THRESHOLD && Math.abs(deltaY) < DRAG_THRESHOLD) return;

  if (kind === 'move' || kind === 'resize') {
    const dates =
      kind === 'move'
        ? movedDates(task, snapshot.axis, deltaX)
        : resizedDates(task, snapshot.axis, deltaX);
    if (dates.start === task.start && dates.finish === task.finish) return;
    callbacks.onTaskDateChange(task.id, dates.start, dates.finish);
    return;
  }

  if (kind === 'progress') {
    callbacks.onTaskProgressChange(task.id, progressFromDrag(bar, deltaX));
    return;
  }

  // kind === 'link'
  const targetIndex = linkTargetRow(
    rowIndex,
    deltaY,
    snapshot.rowHeight,
    snapshot.tasks.length,
  );
  if (targetIndex === null) return;
  const target = snapshot.tasks[targetIndex];
  if (target === undefined) return;
  const targetBar = barGeometry(target, targetIndex, snapshot.axis, {
    rowHeight: snapshot.rowHeight,
    showBaseline: snapshot.showBaseline,
  });
  const sourceAnchor: LinkAnchor = anchor ?? 'finish';
  const chartX = anchorX(bar, sourceAnchor) + deltaX;
  const targetAnchor = anchorAtX(targetBar, chartX);
  callbacks.onDependencyCreate(
    task.id,
    target.id,
    dependencyTypeFor(sourceAnchor, targetAnchor),
  );
}
